import { useEffect, useState } from "react";
import { useSession } from "@/hooks/use-session";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/use-subscription";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { SubscriptionSection } from "@/components/profile/SubscriptionSection";
import { UsageStats } from "@/components/profile/UsageStats";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { checkUserSubscription } from "@/utils/subscription";
import { supabase } from "@/integrations/supabase/client";
import { EmailDisplay } from "@/components/profile/EmailDisplay";
import { Skeleton } from "@/components/ui/skeleton";
import { MobileNav } from "@/components/MobileNav";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

const Profile = () => {
  const { session } = useSession();
  const { toast } = useToast();
  const { isSubscribed, checkSubscription, isLoading: subscriptionLoading } = useSubscription();
  const [profileData, setProfileData] = useState<{
    id: string;
    email?: string;
    subscription_status?: string;
    trial_end_date?: string;
    current_period_end?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        // Get session directly from secure cookies to ensure it's valid
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("[Profile] Error fetching session:", sessionError);
          throw sessionError;
        }
        
        const userId = sessionData.session?.user?.id;
        if (!userId) {
          console.log("[Profile] No authenticated user found");
          setIsLoading(false);
          return;
        }

        console.log("[Profile] Fetching profile data for user:", userId);
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", userId)
          .maybeSingle();

        if (error) {
          console.error("[Profile] Error fetching profile:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to load profile data. Please try again.",
          });
          return;
        }

        console.log("[Profile] Profile data fetched:", data);
        setProfileData(data);
      } catch (error) {
        console.error("[Profile] Error in profile fetch:", error);
        
        // Check for CORS-related errors
        if (error instanceof Error && 
            (error.message.includes('cors') || 
             error.message.includes('CORS') || 
             error.message.includes('cookie'))) {
          console.error('[Profile] Possible CORS/cookie issue:', error.message);
        }
        
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [session?.user?.id, toast]);

  if (isLoading) {
    return (
      <div className={cn(
        "container max-w-2xl mx-auto p-4",
        isMobile && "pb-24" // Add padding for mobile nav
      )}>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
        {isMobile && <MobileNav />}
      </div>
    );
  }

  const hasActiveSubscription = isSubscribed;
  
  // Manual refresh button to force subscription check
  const handleManualRefresh = async () => {
    try {
      console.log('[Profile] Manually refreshing subscription status');
      
      // Get current session from secure cookies
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('[Profile] Error fetching session:', sessionError);
        throw sessionError;
      }
      
      if (!sessionData.session) {
        console.error('[Profile] No valid session found in cookies');
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "Please sign in to check your subscription status."
        });
        return;
      }
      
      // Use the direct utility function that only checks subscription, not query limits
      await checkUserSubscription(sessionData.session);
      
      // After verifying with Stripe directly, refresh the context state
      checkSubscription();
      
      toast({
        title: "Subscription Refreshed",
        description: "Your subscription status has been updated."
      });
    } catch (error) {
      console.error('[Profile] Error refreshing subscription:', error);
      
      // Check for CORS-related errors
      if (error instanceof Error && 
          (error.message.includes('cors') || 
           error.message.includes('CORS') || 
           error.message.includes('cookie'))) {
        console.error('[Profile] Possible CORS/cookie issue:', error.message);
      }
      
      toast({
        variant: "destructive",
        title: "Refresh Failed",
        description: "Could not refresh your subscription status. Please try again."
      });
    }
  }

  return (
    <div className={cn(
      "container max-w-2xl mx-auto p-4 space-y-8",
      isMobile && "pb-24" // Add padding for mobile nav
    )}>
      <ProfileHeader />
      
      <div className="space-y-6 mt-6">
        {isLoading ? (
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <Skeleton className="h-6 w-full" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            <Card>
              <CardContent className="pt-6">
                <EmailDisplay email={session?.user?.email || ""} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <SubscriptionSection 
                  profileData={profileData}
                  hasActiveSubscription={hasActiveSubscription} 
                />
                
                {/* Usage Stats */}
                {!hasActiveSubscription && (
                  <div className="mt-4 border-t pt-4">
                    <UsageStats profileData={profileData} />
                  </div>
                )}
                
                {/* Subscription refresh option */}
                <div className="mt-4 border-t pt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleManualRefresh}
                    className="w-full"
                  >
                    Refresh Subscription Status
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
      {isMobile && <MobileNav />}
    </div>
  );
};

export default Profile;
